/**
 * Returns `true` if the HTML string contains a legacy console block
 * (`<div class="console">`), indicating migration is needed.
 */
export function containsZyConsole(html: string): boolean {
    return /<div[^>]*\bclass="[^"]*\bconsole\b[^"]*"[^>]*>/i.test(html);
}

/**
 * Replaces legacy `<div class="console"><pre>` blocks with `<zyConsole>` elements.
 * Derives `preserve-white-space` from the `<pre>` style and `highlight` from
 * `<span class="codehighlight">` line numbers. Content is not re-escaped since
 * it is already HTML-encoded in the source.
 *
 * See `zyconsole-migration.test.ts` for input/output examples.
 *
 * @param html - HTML string potentially containing legacy console blocks.
 * @returns HTML string with legacy console blocks replaced by `<zyConsole>` elements.
 */
export async function migrateZyConsoleFormat(html: string): Promise<string> {
    const consoleRegex = /<div[^>]*\bclass="[^"]*\bconsole\b[^"]*"[^>]*><pre([^>]*)>([\s\S]*?)<\/pre><\/div>/gi;

    return html.replace(consoleRegex, (_match, preAttrs: string, preContent: string) => {
        // Negative lookahead prevents matching "pre-wrap", "pre-line", etc.
        const preserveWhiteSpace = /white-space\s*:\s*pre(?![a-zA-Z-])/i.test(preAttrs);

        const rawLines = preContent.split('\n');
        const highlightedLineNumbers: number[] = [];
        const cleanLines: string[] = [];

        rawLines.forEach((line, idx) => {
            if (/<span[^>]*\bclass="[^"]*\bcodehighlight\b[^"]*"[^>]*>/i.test(line)) {
                highlightedLineNumbers.push(idx + 1);
            }
            cleanLines.push(line.replace(/<[^>]+>/g, ''));
        });

        const attrs: string[] = [];
        if (preserveWhiteSpace) attrs.push('preserve-white-space="true"');
        if (highlightedLineNumbers.length > 0) attrs.push(`highlight="${highlightedLineNumbers.join(',')}"`);

        // The pre content is already HTML-encoded; no further escaping needed.
        const attrStr = attrs.length > 0 ? ' ' + attrs.join(' ') : '';
        return `<zyConsole${attrStr}>${cleanLines.join('\n')}</zyConsole>`;
    });
}
